import { HTMLTagsUsingDOMParser } from "../../../../utils/Helper";
import { profileStatus, CLAIMED } from "./profileStatus";

/* Reading the Business Details tab. */

/* Transcribed from BriefCompanyDetails: a CLAIMED company's lists are filtered to rows that have not been soft-deleted; every other status shows the lists as... */
const KEEP = {
  company_tax: (item) => item?.is_deleted === 0,
  bankDetails: (item) => item?.bankID?.is_deleted === 0,
  ownershipCompany: (item) =>
    item?.ownershipID?.ownership_compID?.is_deleted === 0 ||
    item?.ownershipID?.ownership_indID?.is_deleted === 0,
  authorizedSignDetails: (item) => item?.Authorized_signID?.is_deleted === 0,
  companyKeyPersonnel: (item) => item?.key_personnelID?.is_deleted === 0,
  relatedEntity: (item) => item?.related_entityID?.is_deleted === 0,
  businessRelation: (item) => item?.business_relationID?.is_deleted === 0,
  insuranceDetails: (item) => item?.insuranceID?.is_deleted === 0,
};

export const visibleList = (company, key) => {
  const list = company?.[key] || [];
  if (profileStatus(company) !== CLAIMED) return list;
  const keep = KEEP[key];
  return keep ? list.filter(keep) : list;
};

/* ── Registration ──────────────────────────────────────────────────────── */

/* Transcribed: the whole card is suppressed unless at least one of these is
   present. */
export const hasRegistration = (company) =>
  Boolean(
    company?.registration_body ||
      company?.legal_form ||
      company?.date_of_issue ||
      company?.activities ||
      company?.capital ||
      company?.date_of_expiry ||
      company?.license_no ||
      company?.trade_license
  );

/* `activities` is one HTML blob, not structured rows. */
export const permittedActivities = (activities) => {
  if (!activities) return [];
  const text = HTMLTagsUsingDOMParser(String(activities));
  return text
    .split(/\r?\n|,(?=\s*[A-Z0-9])/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const match = line.match(/^(\d[\d.]*)\s+(.*)$/);
      return match ? { code: match[1], label: match[2] } : { code: null, label: line };
    });
};

/* ── Stakeholders ──────────────────────────────────────────────────────── */

/* Transcribed: an ownership row carries either a company owner or an individual owner, and the live item reads whichever is present — the INDIVIDUAL first,... */
export const stakeholderRows = (company) =>
  visibleList(company, "ownershipCompany")
    .map((item) => {
      const owner =
        item?.ownershipID?.ownership_indID || item?.ownershipID?.ownership_compID;
      if (!owner) return null;

      const type = String(owner?.type || "").toLowerCase();
      const isIndividual = type
        ? type === "individual"
        : Boolean(item?.ownershipID?.ownership_indID);

      return {
        id: owner?.ownership_indID || owner?.ownership_compID,
        name: owner?.name || owner?.company_name,
        percentage: Number(owner?.percentage_of_shares_held) || 0,
        kind: isIndividual ? "Individual" : "Company",
        /* Present only when the owner is itself on TREVEX. */
        companyID: owner?.ownershipCompany || undefined,
      };
    })
    .filter((row) => row && row.name);

/* The stacked bar's segments. Widths are the shares as they come; nothing is
   normalised to 100, because a company whose declared shares do not add up is
   a fact worth seeing rather than one to paper over. */
export const stakeholderSegments = (rows = []) => {
  const total = rows.reduce((sum, r) => sum + (r.percentage || 0), 0);
  if (total <= 0) return [];
  return rows.map((row, i) => ({
    ...row,
    /* Five tones, then it wraps. */
    tone: `var(--stake-${(i % 5) + 1})`,
    width: (row.percentage / total) * 100,
  }));
};

/* ── People ────────────────────────────────────────────────────────────── */

/* Transcribed mobile formatting: the pipes the API stores are stripped. */
const cleanMobile = (value) =>
  value ? String(value).replace(/\|/g, "").trim() : undefined;

export const signatoryRows = (company) =>
  visibleList(company, "authorizedSignDetails")
    .map((item) => item?.Authorized_signID)
    .filter(Boolean)
    .map((s) => ({
      name: s?.name,
      title: s?.designation,
      email: s?.work_email,
      mobile: cleanMobile(s?.work_mobile),
      nationality: s?.nationality,
    }))
    .filter((s) => s.name);

export const personnelRows = (company) =>
  visibleList(company, "companyKeyPersonnel")
    .map((item) => item?.key_personnelID)
    .filter(Boolean)
    .map((p) => ({
      name: p?.name,
      designation: p?.designation,
      email: p?.work_email,
      mobile: cleanMobile(p?.work_mobile),
    }))
    .filter((p) => p.name);

/* ── Related entities and business relations, merged ───────────────────── */

/* The design gives these one table. */
/* How a relation type reads in the pill. */
export const relationLabel = (relation) => {
  const text = String(relation ?? "").replace(/_/g, " ").trim();
  if (text === "") return undefined;
  return text.replace(/(^|\s)(\S)/g, (_, lead, letter) => lead + letter.toUpperCase());
};

export const relationRows = (company) => {
  const entities = visibleList(company, "relatedEntity").map((item) => {
    const e = item?.related_entityID;
    return {
      source: "entity",
      name: e?.companyName,
      /* Transcribed: a related entity links out by its TREVEX companyID when
         it has one — the WIN is a reference, not a route. */
      companyID: e?.companyID?.companyID,
      win: e?.companyWIN,
      state: e?.state,
      country: e?.country,
      /* Same treatment as the business-relation branch below. This one was
         printing the raw type, underscores and all — "corporate_office". */
      relation: relationLabel(e?.relation_type),
    };
  });

  const relations = visibleList(company, "businessRelation").map((item) => {
    const r = item?.business_relationID;
    return {
      source: "relation",
      /* Transcribed fallback: the relation's own name, or the linked
         company's registered name. */
      name: r?.registered_company_name || r?.companyID?.registered_company_name,
      companyID: r?.companyID?.companyID,
      state: r?.state,
      country: r?.country,
      /* The live row replaces underscores; relationLabel also capitalises, so
         both branches of this table read the same way. */
      relation: relationLabel(r?.relation_type),
    };
  });

  return [...entities, ...relations].filter((row) => row.name);
};

/* An abbreviation for the row's leading cell, as the design shows. Derived,
   because nothing in the payload stores one: initials of the first four words,
   which is what the design's examples are (Blue Horizon Tech Ventures → BHTV). */
export const abbreviate = (name) => {
  const words = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return "";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return words
    .slice(0, 4)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
};

/* The relation pill's tone. Named groups rather than a hash, so the same
   relation reads the same colour on every profile. */
const RELATION_TONES = [
  [/parent/i, "coral"],
  [/subsidiar/i, "navy"],
  [/branch/i, "violet"],
  [/customer|client/i, "green"],
  [/supplier|vendor/i, "gold"],
  [/sub.?contractor/i, "coral"],
  [/service/i, "teal"],
];

export const relationTone = (relation) => {
  const found = RELATION_TONES.find(([pattern]) => pattern.test(String(relation || "")));
  return found ? found[1] : "neutral";
};

/* ── Banks, tax, insurance, financials ─────────────────────────────────── */

export const bankRows = (company) =>
  visibleList(company, "bankDetails")
    .map((item) => item?.bankID)
    .filter(Boolean)
    .map((b) => ({ name: b?.bank_name, iban: b?.IBAN, country: b?.country }))
    .filter((b) => b.name);

export const taxRows = (company) =>
  visibleList(company, "company_tax")
    .map((t) => ({
      label: t?.categoryTitle,
      number: t?.tax_reg_no,
      file: t?.upload_file,
    }))
    .filter((t) => t.number || t.label);

export const insuranceRows = (company) =>
  visibleList(company, "insuranceDetails")
    .map((item) => item?.insuranceID)
    .filter(Boolean)
    .map((i) => ({
      provider: i?.insurer,
      type: i?.type,
      /* Transcribed fallback: the broker's linked company name, or the free
         text broker field. */
      broker: i?.broker_company_ID?.registered_company_name || i?.broker,
      insurerCompanyID: i?.insurer_company_ID?.companyID,
    }))
    .filter((i) => i.provider || i.type);

/* Transcribed: the live tab reads financialDetails[0] only. */
export const financialAdmin = (company) => {
  const f = company?.financialDetails?.[0];
  if (!f) return null;
  return {
    yearEnd: f?.financial_year_end,
    lastAudited: f?.last_audited,
    /* Transcribed fallback: the audit firm's linked company, or its name. */
    auditFirm: f?.audit_firm_company?.registered_company_name || f?.audit_firm_name,
    state: f?.state,
    country: f?.country,
  };
};
